<script setup>
import { computed, ref, reactive, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { SsoSDK as QAUTH_SSO_SDK } from '@q-auth/auth-sdk'
import { useStore } from 'vuex'
import { readHeaderSession } from '@/utils/headerSession'
import { setTrack } from '@/api/login'
import { startLogin } from '@/utils/startLogin'
import { logoutFromSso } from '@/utils/ssoLogout'
import Banner from './Banner.vue'
import optionButton from './RightOption.vue'
import { FAT_AUTH_ORIGIN, FAT_CLIENT_ID, QAUTH_POOL_CODE } from '@/utils/oauth'

const router = useRouter()
const route = useRoute()
const props = defineProps({
  guestDesign: { type: Boolean, default: false },
  designAlways: { type: Boolean, default: false },
  hideBanner: { type: Boolean, default: false },
})
const emit = defineEmits(['auth-change'])
const store = useStore()
const user = ref('')
const isLoggedIn = ref(false)
const guestHeader = computed(
  () => props.designAlways || (props.guestDesign && !isLoggedIn.value),
)
const guestLogo = `${import.meta.env.BASE_URL}image/home-redesign/54bbec266273057caf98f198.svg`
const guestNav = ref(null)
const openGuestMenu = ref('')
const toggleGuestMenu = (name) => {
  openGuestMenu.value = openGuestMenu.value === name ? '' : name
}
const closeGuestMenuOutside = (event) => {
  if (!guestNav.value?.contains(event.target)) openGuestMenu.value = ''
}
const closeGuestMenuOnEscape = (event) => {
  if (event.key === 'Escape' && openGuestMenu.value) {
    const trigger = guestNav.value?.querySelector('[aria-expanded="true"]')
    openGuestMenu.value = ''
    trigger?.focus()
  }
}
watch(
  () => route.fullPath,
  () => {
    openGuestMenu.value = ''
  },
)
watch(isLoggedIn, (value) => emit('auth-change', value), { immediate: true })
const ssoSdk = ref(null)
const ssoCode = QAUTH_POOL_CODE
const coreRoute = ['edge_ai', 'cloud_ai', 'audio_ai', 'vision_ai']
const routeName = route.name
const routeSource = route.path.slice(11)
const uniclawRoute = ['uniclaw', 'uniclawFlash', 'uniclawAuth', 'uniclawDocs']

const showPlatformName = ref(false)
const industryPullDown = ref(null)

const readLoginStatus = () => {
  const session = readHeaderSession()
  user.value = session.displayName
  isLoggedIn.value = session.loggedIn
}

const handleLogin = () => {
  ssoSdk.value = startLogin(ssoSdk.value)
}

const isLoggingOut = ref(false)
const handleLogout = async () => {
  if (isLoggingOut.value) return
  isLoggingOut.value = true
  try {
    await logoutFromSso({
      sdk: ssoSdk.value,
      revokeSession: () => store.dispatch('LogOut'),
      onLocalLogout: () => {
        user.value = ''
        isLoggedIn.value = false
      },
    })
  } catch (error) {
    console.error('退出登录失败', error)
    window.alert(error?.message || '退出登录失败，请稍后重试。')
  } finally {
    isLoggingOut.value = false
  }
}

const solutionRoute = [
  'pectorophony',
  'Robrain',
  'openVend',
  'voiceAssistant',
  'businessRobot',
  'smartRetail',
  'aiToys',
  'robotherapist',
]

const industryList = [
  {
    name: 'Robrain AI机器人解决方案',
    key: 'Robrain',
    route: 'solutions/robrain',
  },
  {
    name: 'AI具身理疗机器人解决方案',
    key: 'robotherapist',
    route: 'solutions/robotherapist',
  },
  {
    name: 'AI商业服务机器人解决方案',
    key: 'businessRobot',
    route: 'solutions/service_robot',
  },
  {
    name: 'OpenVending AI智能识别秤解决方案',
    key: 'openVend',
    route: 'solutions/smart_scale',
  },
  {
    name: 'OpenVending AI智能无人零售解决方案',
    key: 'smartRetail',
    route: 'solutions/smart_vending_machine',
  },
  {
    name: 'AI音频模组解决方案',
    key: 'pectorophony',
    route: 'solutions/audio_modules',
  },
  {
    name: 'AI玩具解决方案',
    key: 'aiToys',
    route: 'solutions/smart_toys',
  },
]

const coreList = [
  {
    name: '端侧AI大模型',
    route: 'core_capabilities/edge_ai',
    key: 'edge_ai',
  },
  {
    name: '端云融合AI Agent',
    route: 'core_capabilities/edge_cloud_ai',
    key: 'cloud_ai',
  },
  {
    name: 'AI音频算法',
    route: 'core_capabilities/ai_audio',
    key: 'audio_ai',
  },
  {
    name: 'AI视觉算法',
    route: 'core_capabilities/ai_vision',
    key: 'vision_ai',
  },
]

const goHome = () => {
  // location.href = `${location.protocol}//${location.hostname}/services-solutions/aimarket`
}

// 下拉菜单对应id变量
const pullDown = reactive({
  coreTrigger: null,
  coreTarget: null,
  quectelTrigger: null,
  quectelTarget: null,
})

const handleTriggerMouseEnter = (target) => {
  target.classList.add('show')
}

const handleTriggerMouseLeave = (target) => {
  target.classList.remove('show')
  target.classList.add('show-hold')
}

const handleTriggerClick = (e, target) => {
  e.stopPropagation()

  if (target.classList.contains('show')) {
    target.classList.remove('show')
    target.classList.add('show-hold')
  } else {
    target.classList.add('show')
    target.classList.remove('show-hold')
  }
}

const goToRoute = (routename) => {
  void setTrack({ position: `navigation > ${routename}` })
  router.push(`/${routename}`)
}

const goTool = () => goToRoute('aide')

const checkScreenSize = () => {
  showPlatformName.value = window.innerWidth > 992
}

const handleAddEventListener = (id) => {
  pullDown[`${id}Trigger`] = document.querySelector(`#${id}`)
  pullDown[`${id}Target`] = document.querySelector(`#${id}PullDown`)

  const trigger = pullDown[`${id}Trigger`]
  const target = pullDown[`${id}Target`]
  // 确保元素存在再绑定事件
  if (trigger && target) {
    // 绑定鼠标事件
    trigger.addEventListener('mouseenter', () =>
      handleTriggerMouseEnter(target),
    )
    trigger.addEventListener('mouseleave', () =>
      handleTriggerMouseLeave(target),
    )
    target.addEventListener('mouseenter', () => handleTriggerMouseEnter(target))
    target.addEventListener('mouseleave', () => handleTriggerMouseLeave(target))

    // 绑定点击事件（移动端）
    trigger.addEventListener('click', (e) => handleTriggerClick(e, target))
  } else {
    console.warn(`未找到${id}相关元素，事件绑定失败`)
  }
}

const handleRemoveEventListener = (id) => {
  const trigger = pullDown[`${id}Trigger`]
  const target = pullDown[`${id}Target`]

  if (trigger && target) {
    trigger.removeEventListener('mouseenter', () =>
      handleTriggerMouseEnter(target),
    )
    trigger.removeEventListener('mouseleave', () =>
      handleTriggerMouseLeave(target),
    )
    target.removeEventListener('mouseenter', () =>
      handleTriggerMouseEnter(target),
    )
    target.removeEventListener('mouseleave', () =>
      handleTriggerMouseLeave(target),
    )
    trigger.removeEventListener('click', (e) => handleTriggerClick(e, target))
  }
  pullDown[`${id}Trigger`] = null
  pullDown[`${id}Target`] = null
}

const contactUrl =
  'https://www.quectel.com.cn/contact?pro=QUnlvIDmlL7lubPlj7A&tagStr=NGExMDllYjU2NDc3OGZjZg&proType=QUnlvIDmlL7lubPlj7A'
const handleConsult = () => {
  void Promise.resolve()
    .then(() => setTrack({ position: 'consultation' }))
    .catch(() => {})
}

onMounted(() => {
  window.addEventListener('storage', readLoginStatus)
  window.addEventListener('pageshow', readLoginStatus)
  document.addEventListener('pointerdown', closeGuestMenuOutside)
  document.addEventListener('keydown', closeGuestMenuOnEscape)
  window.addEventListener('resize', checkScreenSize)
  checkScreenSize()

  readLoginStatus()
  if (ssoCode) {
    ssoSdk.value = new QAUTH_SSO_SDK({
      client_id: FAT_CLIENT_ID,
      code: ssoCode,
      sso_origin: FAT_AUTH_ORIGIN,
      // SDK只通知重新读取本站会话，不直接授权，也不覆盖本站用户资料。
      onLogin: readLoginStatus,
      onLogout: readLoginStatus,
    })
  }

  handleAddEventListener('core')
  handleAddEventListener('quectel')
})

onBeforeUnmount(() => {
  window.removeEventListener('storage', readLoginStatus)
  window.removeEventListener('pageshow', readLoginStatus)
  document.removeEventListener('pointerdown', closeGuestMenuOutside)
  document.removeEventListener('keydown', closeGuestMenuOnEscape)
  window.removeEventListener('resize', checkScreenSize)
  ssoSdk.value?.destroy()

  handleRemoveEventListener('quectel')
  handleRemoveEventListener('core')
})
</script>

<template>
  <header v-if="guestHeader" class="guest-site-header">
    <div class="guest-brand-row">
      <router-link
        class="guest-brand"
        to="/home"
        v-track="'navigation > home'"
        aria-label="AI开放平台首页"
      >
        <img :src="guestLogo" width="168" height="48" alt="Quectel 移远通信" />
        <span>AI开放平台</span>
      </router-link>
      <div class="guest-account">
        <template v-if="isLoggedIn">
          <span>{{ user ? `您好，${user}` : '已登录' }}</span>
          <span aria-hidden="true">|</span>
          <a href="#" @click.prevent="handleLogout">退出</a>
        </template>
        <template v-else>
          <a
            href="#"
            @click.prevent="handleLogin"
            v-track="'navigation > login'"
            >登录</a
          >
          <span aria-hidden="true">|</span>
          <a
            class="guest-register"
            v-track="'navigation > register'"
            href="https://www.quectel.com.cn/register"
            >注册</a
          >
        </template>
      </div>
    </div>
    <nav ref="guestNav" class="guest-primary-nav" aria-label="主导航">
      <router-link
        to="/home"
        v-track="'navigation > home'"
        :class="{ 'guest-nav-active': route.name === 'home' }"
        :aria-current="route.name === 'home' ? 'page' : undefined"
        >首页</router-link
      >
      <div
        v-for="menu in [
          { key: 'core', title: '核心能力', items: coreList },
          { key: 'solutions', title: '解决方案', items: industryList },
        ]"
        :key="menu.key"
        class="guest-nav-dropdown"
      >
        <button
          type="button"
          class="guest-dropdown-trigger"
          :class="{
            'guest-nav-active':
              menu.key === 'core'
                ? coreRoute.includes(route.name)
                : solutionRoute.includes(route.name),
          }"
          :aria-expanded="openGuestMenu === menu.key"
          :aria-controls="`guest-menu-${menu.key}`"
          @click="toggleGuestMenu(menu.key)"
        >
          {{ menu.title }}
          <svg
            class="guest-chevron"
            :class="{ 'is-open': openGuestMenu === menu.key }"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="m6 9 6 6 6-6"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <Transition name="guest-menu">
          <div
            v-if="openGuestMenu === menu.key"
            :id="`guest-menu-${menu.key}`"
            class="guest-dropdown-menu"
          >
            <router-link
              v-for="item in menu.items"
              :key="item.key"
              :to="`/${item.route}`"
              v-track="`navigation > ${item.route}`"
              :class="{ 'guest-menu-selected': route.name === item.key }"
              :aria-current="route.name === item.key ? 'page' : undefined"
              @click="openGuestMenu = ''"
              >{{ item.name }}</router-link
            >
          </div>
        </Transition>
      </div>
      <router-link
        to="/algostore"
        v-track="'navigation > algostore'"
        :class="{ 'guest-nav-active': route.path.startsWith('/algostore') }"
        >算法超市</router-link
      >
      <router-link
        to="/uniclaw"
        v-track="'navigation > uniclaw'"
        :class="{
          'guest-nav-active':
            route.path === '/uniclaw' || route.path.startsWith('/uniclaw/'),
        }"
        >UniClaw</router-link
      >
      <router-link
        to="/ai-playground"
        v-track="'navigation > ai-playground'"
        :class="{ 'guest-nav-active': route.path === '/ai-playground' }"
        >AI Playground</router-link
      >
      <router-link
        to="/aide"
        v-track="'navigation > aide'"
        :class="{
          'guest-nav-active':
            route.path === '/aide' ||
            route.path.startsWith('/aide/') ||
            route.path === '/tool',
        }"
        >AI开发工具链</router-link
      >
      <router-link
        to="/memcore/intro"
        v-track="'navigation > memcore/intro'"
        :class="{
          'guest-nav-active':
            route.path === '/memcore' || route.path.startsWith('/memcore/'),
        }"
        >MemCore记忆引擎</router-link
      >
      <router-link
        to="/datahub"
        v-track="'navigation > datahub'"
        :class="{
          'guest-nav-active':
            route.path === '/datahub' || route.path.startsWith('/datahub/'),
        }"
        >DataHub</router-link
      >
      <router-link
        to="/omni-train"
        v-track="'navigation > omni-train'"
        :class="{
          'guest-nav-active':
            route.path === '/omni-train' ||
            route.path.startsWith('/omni-train/'),
        }"
        >开放训练平台</router-link
      >
      <a
        :href="contactUrl"
        target="_blank"
        rel="noopener noreferrer"
        @click="handleConsult"
        >联系我们</a
      >
    </nav>
  </header>
  <div v-else class="header">
    <div class="user-toolbar">
      <div class="login-container">
        <div class="text-white">
          <div class="text-end">
            <span class="me-3 me-sm-4">全国热线：400 960 7678</span>
            <template v-if="!isLoggedIn">
              <a
                href="#"
                @click.prevent="handleLogin"
                v-track="'navigation > login'"
                >登录</a
              >
              <span class="mx-12">|</span>
              <a href="https://www.quectel.com.cn/register">注册</a>
            </template>
            <template v-else>
              <span>{{ user ? `您好，${user}` : '已登录' }}</span>
              <span class="mx-12">|</span>
              <a href="#" @click.prevent="handleLogout">退出</a>
            </template>
          </div>
        </div>
      </div>
    </div>

    <nav class="main-navigation">
      <div class="container">
        <a class="logo" target="_self" @click="goHome">
          <img src="@/assets/logo.svg" width="210px" height="60px" alt="图片" />
          <span v-if="showPlatformName">AI开放平台</span>
        </a>
        <span class="hover-item menu">
          <span
            :class="['nav-item', routeName === 'home' && 'high-light']"
            @click="goToRoute('home')"
          >
            <a target="_self" class="header_link">首页</a>
          </span>
          <span
            :class="[
              'nav-item',
              'arrow',
              'pr-12',
              routeName === 'core' && 'high-light',
              coreRoute.includes(routeName) && 'high-light',
            ]"
            id="core"
          >
            <a target="_self" class="header_link">核心能力</a>
          </span>

          <span
            :class="['nav-item', routeName === 'aimarket' && 'high-light']"
            @click="goToRoute('algostore')"
          >
            <a target="_self" class="header_link">算法超市</a>
          </span>
          <span
            :class="[
              'nav-item',
              uniclawRoute.includes(routeName) && 'high-light',
            ]"
            @click="goToRoute('uniclaw')"
          >
            <a target="_self" class="header_link">UniClaw</a>
          </span>
          <span
            :class="[
              'nav-item',
              [
                'modelTransfer',
                'tool',
                'controlPanel',
                'modelManager',
                'performance',
                'modelInference',
                'aide',
              ].includes(routeName) && 'high-light',
            ]"
            @click="goTool"
          >
            <a target="_self" class="header_link">AI开发工具链</a>
          </span>
          <span
            :class="[
              'nav-item',
              'arrow',
              'pr-12',
              solutionRoute.includes(routeName) && 'high-light',
            ]"
            id="quectel"
          >
            <a target="_self" class="header_link">解决方案</a>
          </span>
          <span class="concat-item">
            <a
              class="tmall"
              :href="contactUrl"
              target="_blank"
              rel="noopener noreferrer"
              @click="handleConsult"
            >
              联系我们
            </a></span
          >
        </span>

        <optionButton />
      </div>
    </nav>
  </div>

  <Banner v-if="!guestHeader && !props.hideBanner" />

  <!-- 核心能力下拉菜单 -->
  <ul
    v-show="!guestHeader"
    id="corePullDown"
    class="nav-sub-container full-col"
    :style="'width:100%;'"
    ref="corePullDown"
  >
    <div class="sub-list-full-col">
      <div class="left">
        <div class="right jusify-center">
          <div class="nav-sub-item">
            <ul class="sub-list-container">
              <ul class="sub-list-container">
                <li
                  v-for="item in coreList"
                  :key="item.name"
                  @click="goToRoute(item.route, 'core')"
                  :class="['fs-16', routeName === item.key && 'high-light']"
                >
                  {{ item.name }}
                </li>
              </ul>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </ul>

  <!-- 解决方案下拉菜单 -->
  <ul
    v-show="!guestHeader"
    id="quectelPullDown"
    class="nav-sub-container full-col"
    :style="'width:100%;'"
    ref="industryPullDown"
  >
    <div class="sub-list-full-col">
      <div class="left">
        <div class="right jusify-center">
          <div class="nav-sub-item">
            <ul class="sub-list-container">
              <li
                v-for="item in industryList"
                :key="item.name"
                @click="goToRoute(item.route, 'solution')"
                :class="['fs-16', routeName === item.key && 'high-light']"
              >
                {{ item.name }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </ul>
</template>

<style lang="scss" scoped>
.guest-site-header {
  position: relative;
  z-index: 30;
  background: #fff;
  color: #14233b;
  font-family:
    Stratos-Regular,
    'Source Han Sans',
    Arial,
    sans-serif,
    -apple-system,
    BlinkMacSystemFont;
}
.guest-site-header *,
.guest-site-header *::before,
.guest-site-header *::after {
  box-sizing: border-box;
}
.guest-brand-row,
.guest-primary-nav {
  max-width: 1440px;
  width: calc(100% - 48px);
  margin: 0 auto;
  display: flex;
  align-items: center;
}
.guest-brand-row {
  height: 64px;
  justify-content: space-between;
  gap: 24px;
}
.guest-brand {
  display: inline-flex;
  align-items: center;
  gap: 24px;
  color: #14233b;
  text-decoration: none;
}
.guest-brand img {
  display: block;
  width: 168px;
  height: 48px;
  object-fit: contain;
}
.guest-brand span {
  font-size: 20px;
  line-height: 30px;
  font-weight: 700;
  letter-spacing: 2px;
}
.guest-account {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 24px;
  color: #617089;
}
.guest-account a {
  color: inherit;
  text-decoration: none;
}
.guest-account .guest-register {
  color: #ed000c;
}
.guest-primary-nav {
  min-height: 62px;
  gap: 48px;
  flex-wrap: wrap;
  padding: 16px 0;
}
.guest-primary-nav > a,
.guest-nav-pending,
.guest-dropdown-trigger {
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  color: #14233b;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
}
.guest-nav-pending {
  cursor: default;
}
.guest-primary-nav .guest-nav-active {
  position: relative;
  color: #ed000c;
}
.guest-nav-active::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -6px;
  width: 100%;
  height: 2px;
  border-radius: 2px;
  background: #ed000c;
}
.guest-dropdown-trigger.guest-nav-active {
  position: relative;
}
.guest-dropdown-trigger.guest-nav-active::after {
  width: 100%;
}
.guest-nav-dropdown {
  position: relative;
  align-self: stretch;
  display: flex;
  align-items: center;
}
.guest-dropdown-trigger {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  padding: 0;
  border: 0;
  background: transparent;
  font-family: inherit;
  transition: color 180ms ease;
}
.guest-dropdown-trigger:hover,
.guest-dropdown-trigger[aria-expanded='true'] {
  color: #ed000c;
}
.guest-chevron {
  flex-shrink: 0;
  transition: transform 220ms ease;
}
.guest-chevron.is-open {
  transform: rotate(180deg);
}
.guest-menu-enter-active,
.guest-menu-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}
.guest-menu-enter-from,
.guest-menu-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
.guest-dropdown-menu {
  position: absolute;
  top: calc(100% + 16px);
  left: -16px;
  width: 340px;
  max-width: calc(100vw - 32px);
  background: #fff;
  padding: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 12px 30px #14233b18;
}
.guest-dropdown-menu a {
  display: block;
  padding: 10px 12px;
  color: #14233b;
  text-decoration: none;
  font-size: 15px;
  line-height: 24px;
  overflow-wrap: anywhere;
  border-radius: 4px;
  transition:
    color 160ms ease,
    background-color 160ms ease;
}
.guest-dropdown-menu a:hover {
  background: #f6f8fc;
}
.guest-dropdown-menu a.guest-menu-selected {
  color: #ed000c;
}
.guest-dropdown-menu a:hover,
.guest-primary-nav a:hover {
  color: #ed000c;
}
.guest-site-header a:focus-visible,
.guest-dropdown-trigger:focus-visible {
  outline: 2px solid #ed000c;
  outline-offset: 4px;
}
@media (prefers-reduced-motion: reduce) {
  .guest-chevron,
  .guest-menu-enter-active,
  .guest-menu-leave-active,
  .guest-dropdown-trigger,
  .guest-dropdown-menu a {
    transition: none;
  }
}
@media (max-width: 1500px) {
  .guest-primary-nav {
    gap: 24px;
  }
}
@media (max-width: 767px) {
  .guest-brand-row,
  .guest-primary-nav {
    width: calc(100% - 32px);
  }
  .guest-brand {
    gap: 8px;
  }
  .guest-brand img {
    width: 105px;
    height: 36px;
  }
  .guest-brand span {
    display: none;
  }
  .guest-brand-row {
    gap: 8px;
  }
  .guest-account {
    gap: 5px;
    flex-shrink: 0;
  }
  .guest-primary-nav {
    gap: 12px 20px;
    padding: 12px 0;
  }
  .guest-dropdown-menu {
    width: 256px;
    top: calc(100% + 12px);
  }
}
@media (max-width: 992px) {
  .guest-brand span {
    display: none;
  }
}
@media (min-width: 1200px) {
  .guest-primary-nav > a,
  .guest-nav-pending,
  .guest-dropdown-trigger {
    font-size: 15px;
  }
}
.mr10 {
  margin-right: 10px;
  float: left;
}
.pd-10 {
  margin-top: 5px;
}
.pr-12 {
  padding-right: 12px;
}
.mt50 {
  margin-top: 34px !important;
  &:hover {
    color: #272727 !important;
  }
}
.mt10 {
  margin-top: 10px;
}
.mt20 {
  margin-top: 20px !important;
}
.ml-5 {
  margin-left: 5px !important;
}
.ml-10 {
  margin-left: 10px !important;
  border-radius: 6px !important;
}
.fs-16 {
  font-size: 16px !important;
  margin-bottom: 16px !important;
}
.ht-323 {
  height: 280px !important;
}
.jusify-center {
  justify-content: center !important;
}
.mt100 {
  margin-top: 100px !important;
  font-weight: 700 !important;
  font-size: 16px !important;
  cursor: text !important;
  color: #000 !important;
  &:hover {
    color: #000 !important;
  }
}

.nav-center {
  text-align: center;
  padding: 10px 0;
  font-size: 15px;
  cursor: pointer;

  &:hover {
    color: #ff0000;
  }
}

h1 {
  color: #f56c6c;
}
.header {
  position: fixed;
  height: 111px;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background-color: #fff;
  display: flex;
  flex-direction: column;

  @media screen and (min-width: 576px) {
    .me-sm-4 {
      margin-right: 1.5rem !important;
    }
  }
  @media (min-width: 576px) {
    .login-container,
    .container {
      max-width: 540px !important;
    }
  }
  @media (min-width: 768px) {
    .login-container,
    .container {
      max-width: 720px !important;
    }
  }
  @media (min-width: 992px) {
    .login-container,
    .container {
      max-width: 960px !important;
    }
    .hover-item {
      display: flex !important;
    }
  }
  @media (min-width: 1200px) {
    .login-container,
    .container {
      max-width: 1140px !important;
    }
    .nav-item,
    .concat-item {
      a {
        font-size: 16px !important;
      }
    }
  }
  @media (min-width: 1400px) {
    .login-container,
    .container {
      max-width: 1320px !important;
    }
  }
  @media (min-width: 1500px) {
    .login-container,
    .container {
      max-width: 1440px !important;
    }
  }
  @media (max-width: 991px) {
    .menu {
      display: none;
    }
  }
  @media (min-width: 991px) {
    .menu {
      display: inline-block;
    }
  }

  .user-toolbar {
    padding: 5px 0;
    line-height: 24px;
    background-color: #000000;
    font-weight: 300;
    .login-container {
      // width: 100%;
      margin-right: auto;
      margin-left: auto;
      padding-right: calc(3rem * 0.5);
      padding-left: calc(3rem * 0.5);
      .text-white {
        font-size: 14px;
        color: rgba(255, 255, 255, 1);
        .text-end {
          text-align: right;
          .me-3 {
            margin-right: 1rem;
          }
          a {
            text-decoration: none;
            color: rgba(255, 255, 255, 1);
            &:hover {
              opacity: 0.8;
            }
          }
          .mx-12 {
            margin: 1px 9px 0 9px;
          }
        }
      }
    }
  }

  nav.main-navigation {
    flex-grow: 1;

    .logo {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-wrap: nowrap;
      float: left;

      img {
        padding-right: 16px;
      }

      span {
        height: 32px;
        line-height: 32px;
        font-size: 23px;
        color: #1a1a1a;
        font-weight: 600;
        white-space: nowrap;
      }

      .text {
        border-right: none;
        padding-right: 0;
        height: 100%;
        line-height: 64px;
      }
    }

    .container {
      width: 100%;
      margin-right: auto;
      margin-left: auto;
      padding-right: 4px;
      height: 100%;
      box-sizing: border-box;
      display: flex;
      justify-content: space-between;
      padding-right: calc(3rem * 0.5);
      padding-left: calc(3rem * 0.5);

      .nav-item {
        margin-left: 40px;
        display: flex;
        align-items: center;
        position: relative;
        height: 100%;
        cursor: pointer;
        a {
          color: #000;
          white-space: nowrap;
        }

        a,
        a:active,
        a:focus,
        a:hover,
        a:link,
        a:visited {
          text-decoration: none;
        }

        &:first-child {
          margin-left: 0;
        }
      }

      .high-light {
        a {
          color: #ed000c;
        }

        &.arrow {
          &::before {
            border-top: 4px solid #ed000c;
          }
        }
        &.nav-item:after {
          background: #ed000c;
        }
      }

      .concat-item {
        margin-left: 40px;
        display: flex;
        align-items: center;
        position: relative;
        float: right;
        height: 100%;

        a {
          white-space: nowrap;
          font-size: 14px;
        }
      }

      .tmall {
        display: inline-block;
        border: 2px solid #000;
        border-radius: 4px;
        padding: 8px 16px;
        transition: all 0.3s cubic-bezier(0.15, 0.83, 0.66, 1);
        font-size: 16px;
        &:hover {
          transform: scale(1.05);
        }
      }

      .hover-item {
        position: relative;
        // display: flex;
        height: 100%;
        float: left;
        margin-left: 30px;
      }

      .option {
        width: 88px;
      }

      .nav-item {
        &:after {
          content: ' ';
          height: 4px;
          background: transparent;
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          border-radius: 2px 2px 0 0;
          transition: all 0.2s;
          -webkit-transition: all 0.2s;
        }

        &:hover:after {
          content: ' ';
          height: 4px;
          background: #ed000c;
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          border-radius: 2px 2px 0 0;
          transition: all 0.2s;
          -webkit-transition: all 0.2s;
        }

        &.item-disabled:hover:after {
          background: transparent;
        }
      }

      .arrow::before {
        content: '';
        position: absolute;
        width: 0;
        height: 0;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-top: 4px solid #000;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        -webkit-transform: translateY(-50%);
      }

      .nav-console-container {
        float: right;
        height: 64px;

        .lang {
          float: left;
          margin-right: 10px;
          margin-top: 16px;
        }
      }

      .disabled {
        color: rgba(0, 0, 0, 0.6) !important;
      }
    }
  }
}

.show {
  transform: translateY(60px) !important;
}

.show-hold {
  transform: translateY(-25px); /* 初始位置在顶部栏上方 */
}

.nav-sub-container {
  background-color: #fff;
  border-radius: 0 0 4px 4px;
  position: fixed;
  top: 50px;
  left: 0;
  white-space: nowrap;
  // height: 400px;
  box-shadow: 0 16px 16px 0 rgba(39, 39, 39, 0.03);
  color: #000;
  transform: translateY(-110%); /* 初始位置在顶部栏上方 */
  transition: transform 0.3s ease-out;
  z-index: 110;
}

.sub-list-full-col {
  // height: 484px;
  display: flex;
  justify-content: stretch;
  -webkit-box-align: stretch;
  align-items: stretch;
  background: #e9ecec;

  .left {
    width: 100%;
    padding: 35px 24px;
    display: flex;
    justify-content: center;
  }

  .right {
    width: 100%;
    display: flex;
    justify-content: space-between;
    max-width: 1400px;
    padding: 0 50px;
    .nav-sub-item {
      flex: 1;
      margin-right: 20px;
      display: inline-block;
      font-size: 13px;
      vertical-align: top;
      max-width: 140px;

      .nav-sub-item-title {
        font-weight: 700;
        cursor: text;
        line-height: 22px;
        margin: 0px 0 14px;
        display: block;
        font-size: 16px;
        padding-bottom: 14px;
        border-bottom: 1px solid #dadada;
      }

      .sub-list-container {
        position: relative;
        display: inline-block;
        color: #000;
        vertical-align: bottom;
        width: 100%;
        li {
          display: flex;
          margin-bottom: 10px;
          cursor: pointer;
          font-weight: 400;
          font-size: 14px;
          color: #1a1a1a;
        }

        li:last-child {
          margin-bottom: 0 !important;
        }

        li:hover {
          color: #ed000c;
        }

        .fs-16.high-light {
          color: #ed000c;
        }
      }
    }
  }
}

li {
  list-style: none;
}

.new-tag {
  position: relative;
  top: -1px;
  width: 42px;
  height: 25px;
  left: 9px;
  pointer-events: none;
}

.new-tag-text {
  color: #666666;
  margin-left: 15px;
}

.hot-tag-text {
  color: #ff0000;
  margin-left: 15px;
}

a,
a:hover {
  text-decoration: none;
}

a {
  color: #000000;
  font-weight: 400;
  font-size: 14px;
}

.card {
  margin-top: 20px;
}

:deep() {
  .ant-tabs .ant-tabs-nav {
    width: 127px !important;
  }

  .ant-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    font-size: 16px;
  }

  .ant-btn-text {
    color: #fff;
  }

  .ant-tabs-nav-more {
    display: none;
  }

  .ant-tabs-nav-wrap {
    width: 128px;
  }

  .sub-list-container li {
    font-size: 14px;
  }
}
</style>
